export type CardConfig = {
  name: string;
  isEditable: boolean;
  path?: string;
  /**
   * Hard character limit for free-text cards, mirroring the Postgres column the value lands in.
   *
   * The API rejects anything longer (PatientFieldConstraints / PatientDetailsUpdateRequestValidator),
   * but that rejection arrives as a toast *after* the panel has closed and discarded the edits, so
   * without this the user loses their work to a limit they were never shown. Keep these in lockstep
   * with the API constants.
   *
   * Omitted for dropdowns, date and number inputs, which cannot overflow.
   */
  maxLength?: number;
};

/**
 * Card name -> the key holding the label the user reads. The name stays in English because it is
 * the card's identity: it keys the save payload, the persisted card order and the per-field
 * validation. Both the panel and the Confirm changes modal resolve the label through here, so the
 * two can never drift apart (EN-5191).
 */
export const CARD_LABEL_KEYS: Record<string, string> = {
  "First name": "patientPage_cards_first_name",
  "Last name": "patientPage_cards_last_name",
  DOB: "patientPage_cards_dob",
  "App version": "patientPage_cards_app_version",
  "Hospital ID": "patientPage_cards_hospital_id",
  "PMP ID": "patientPage_cards_pmp_id",
  Email: "patientPage_cards_email",
  "Last Review": "patientPage_cards_last_review",
  "Last Reviewed By": "patientPage_cards_last_reviewed_by",
  "Last Used": "patientPage_cards_last_used",
  "Weight (kg)": "patientPage_cards_weight_kg",
  "Height (cm)": "patientPage_cards_height_cm",
  Country: "patientPage_cards_country",
  "Caregiver name": "patientPage_cards_caregiver_name",
  "Caregiver phone number": "patientPage_cards_caregiver_phone_number",
  Goals: "patientPage_cards_goals",
  Phone: "patientPage_cards_phone",
  Gender: "patientPage_cards_gender",
  Ethnicity: "patientPage_cards_ethnicity",
  Allergies: "patientPage_cards_allergies",
  Diagnosis: "patientPage_cards_diagnosis",
};

/** Looks a card's label key up by its name; undefined for a card with no fixed label (custom fields). */
export const getCardLabelKey = (name: string): string | undefined =>
  CARD_LABEL_KEYS[name];

export const cardsCofigs: CardConfig[] = [
  {
    name: "First name",
    isEditable: true,
    path: "/first_name",
    // cms_user.first_name varchar(30)
    maxLength: 30,
  },
  {
    name: "Last name",
    isEditable: true,
    path: "/last_name",
    // cms_user.last_name varchar(30)
    maxLength: 30,
  },
  {
    name: "DOB",
    isEditable: true,
    path: "/dob",
  },
  {
    name: "App version",
    isEditable: false,
    path: "/app_Version",
  },
  {
    name: "Hospital ID",
    isEditable: true,
    path: "/hospital_patient_id",
    // cms_patient.hospital_patient_id varchar(70)
    maxLength: 70,
  },
  {
    // EN-4960: edited inline as a multi-select. The save is routed to PUT /Wards (not the
    // /hospital_id JSON patch) by PatientLateralMenu, so the full ward set is preserved and the
    // primary is derived server-side.
    name: "Ward",
    isEditable: true,
    path: "/hospital_id",
  },
  {
    name: "Email",
    isEditable: true,
    path: "/email",
    // cms_user.email varchar(254)
    maxLength: 254,
  },
  {
    name: "Phone",
    isEditable: true,
    path: "/phone_number",
    // cms_user.phone_number varchar(30)
    maxLength: 30,
  },
  {
    name: "Weight (kg)",
    isEditable: true,
    path: "/weight",
  },
  {
    name: "Height (cm)",
    isEditable: true,
    path: "/height",
  },
  {
    name: "Country",
    isEditable: true,
    path: "/country_id",
  },
  {
    name: "Caregiver name",
    isEditable: false,
    path: "/caregiver_name",
  },
  {
    name: "Caregiver phone number",
    isEditable: false,
    path: "/caregiver_phone_number",
  },
  {
    name: "Gender",
    isEditable: true,
    path: "/sex",
  },
  {
    name: "Ethnicity",
    isEditable: true,
    path: "/ethnicity",
  },
  {
    name: "Allergies",
    isEditable: true,
    path: "/allergies",
  },
  {
    name: "Diagnosis",
    isEditable: true,
    path: "/diagnoses",
  },
];
